import { useEffect, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND
} from 'lexical'
import { $setBlocksType } from '@lexical/selection'
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND
} from '@lexical/list'
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Divider from '@mui/material/Divider'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import FormatBold from '@mui/icons-material/FormatBold'
import FormatItalic from '@mui/icons-material/FormatItalic'
import FormatUnderlined from '@mui/icons-material/FormatUnderlined'
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough'
import Code from '@mui/icons-material/Code'
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenter from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRight from '@mui/icons-material/FormatAlignRight'
import FormatAlignJustify from '@mui/icons-material/FormatAlignJustify'
import FormatListBulleted from '@mui/icons-material/FormatListBulleted'
import FormatListNumbered from '@mui/icons-material/FormatListNumbered'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'

export default function RteToolbarPlugin () {
  const [editor] = useLexicalComposerContext()
  const [activeFormats, setActiveFormats] = useState([])
  const [blockType, setBlockType] = useState('paragraph')

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          const formats = []
          if (selection.hasFormat('bold')) formats.push('bold')
          if (selection.hasFormat('italic')) formats.push('italic')
          if (selection.hasFormat('underline')) formats.push('underline')
          if (selection.hasFormat('strikethrough')) formats.push('strikethrough')
          if (selection.hasFormat('code')) formats.push('code')
          setActiveFormats(formats)
        }
      })
    })
  }, [editor])

  const handleFormatChange = (event, newFormats) => {
    const added = newFormats.filter(f => !activeFormats.includes(f))
    const removed = activeFormats.filter(f => !newFormats.includes(f))

    added.forEach(format => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
    })
    removed.forEach(format => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
    })
  }

  const handleBlockTypeChange = (event) => {
    const type = event.target.value
    setBlockType(type)

    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        if (type === 'h1' || type === 'h2' || type === 'h3') {
          $setBlocksType(selection, () => $createHeadingNode(type))
        } else if (type === 'quote') {
          $setBlocksType(selection, () => $createQuoteNode())
        } else if (type === 'ul') {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND)
        } else if (type === 'ol') {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND)
        }
      }
    })
  }

  return (
    <Toolbar
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        gap: 1,
        flexWrap: 'wrap',
        minHeight: 'auto !important',
        py: 1,
        backgroundColor: '#222'
      }}
    >
      <Select
        value={blockType}
        onChange={handleBlockTypeChange}
        size='small'
        sx={{ minWidth: 120 }}
      >
        <MenuItem value='paragraph'>Paragraph</MenuItem>
        <MenuItem value='h1'>Heading 1</MenuItem>
        <MenuItem value='h2'>Heading 2</MenuItem>
        <MenuItem value='h3'>Heading 3</MenuItem>
        <MenuItem value='quote'>Quote</MenuItem>
        <MenuItem value='ul'>Bullet List</MenuItem>
        <MenuItem value='ol'>Numbered List</MenuItem>
      </Select>

      <Divider orientation='vertical' flexItem />

      <ToggleButtonGroup
        value={activeFormats}
        onChange={handleFormatChange}
        size='small'
      >
        <ToggleButton value='bold'>
          <Tooltip title='Bold'>
            <FormatBold />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value='italic'>
          <Tooltip title='Italic'>
            <FormatItalic />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value='underline'>
          <Tooltip title='Underline'>
            <FormatUnderlined />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value='strikethrough'>
          <Tooltip title='Strikethrough'>
            <FormatStrikethroughIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value='code'>
          <Tooltip title='Code'>
            <Code />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation='vertical' flexItem />

      <Box>
        <Tooltip title='Align Left'>
          <IconButton
            size='small'
            onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
          >
            <FormatAlignLeft />
          </IconButton>
        </Tooltip>
        <Tooltip title='Align Center'>
          <IconButton
            size='small'
            onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
          >
            <FormatAlignCenter />
          </IconButton>
        </Tooltip>
        <Tooltip title='Align Right'>
          <IconButton
            size='small'
            onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
          >
            <FormatAlignRight />
          </IconButton>
        </Tooltip>
        <Tooltip title='Justify'>
          <IconButton
            size='small'
            onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}
          >
            <FormatAlignJustify />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider orientation='vertical' flexItem />

      <Box>
        <Tooltip title='Bullet List'>
          <IconButton
            size='small'
            onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND)}
          >
            <FormatListBulleted />
          </IconButton>
        </Tooltip>
        <Tooltip title='Numbered List'>
          <IconButton
            size='small'
            onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND)}
          >
            <FormatListNumbered />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider orientation='vertical' flexItem />

      <Box>
        <Tooltip title='Undo'>
          <IconButton
            size='small'
            onClick={() => editor.dispatchCommand(UNDO_COMMAND)}
          >
            <UndoIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Redo'>
          <IconButton
            size='small'
            onClick={() => editor.dispatchCommand(REDO_COMMAND)}
          >
            <RedoIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Toolbar>
  )
}